import { Popper } from '@mui/base/Popper';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { ControlledFormInputAutocomplete } from '@components/formInputs/ControlledFormInputAutocomplete';
import { FormListbox } from '@components/formListbox/FormListbox';
import { IconButton } from '@components/iconButton/IconButton';
import React, { useState, type HTMLAttributes } from 'react';
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { type PlaceAutocompleteSuggestion } from './apiHelpers';
import styles from './GooglePlaceAutocompleteInput.scss';
import { useGooglePlaceAutocomplete } from './useGooglePlaceAutocomplete';

// Workaround for getOptionProps not being typed correctly.
type GetOptionsType = HTMLAttributes<HTMLLIElement> & { key: string };

const suggestionLabel = (
  option: PlaceAutocompleteSuggestion | string,
): string => (typeof option === 'string' ? option : option.address);

const suggestionKey = (option: PlaceAutocompleteSuggestion | string): string =>
  typeof option === 'string' ? option : option.placeId;

export interface GooglePlaceAutocompleteInputProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  name: Name;
  label: string;
  placeholder?: string;
  labelClassName?: string;
  darkMode?: boolean;
  restaurantId: string;
}

/**
 * Editable, optional address field backed by Google Places Autocomplete,
 * proxied through our own API (so no Google Maps API key is exposed to, or
 * required by, the browser).
 */
export const GooglePlaceAutocompleteInput = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  control,
  setValue,
  name,
  label,
  placeholder,
  labelClassName,
  darkMode,
  restaurantId,
}: GooglePlaceAutocompleteInputProps<T, Name>) => {
  const { suggestions, loading, search, clearSuggestions, resolvePlace } =
    useGooglePlaceAutocomplete(restaurantId);
  const currentValue =
    (useWatch({ control, name }) as string | undefined) ?? '';
  const [isSearching, setIsSearching] = useState(false);

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    popupOpen,
    anchorEl,
    setAnchorEl,
    groupedOptions,
    getClearProps,
  } = useAutocomplete<PlaceAutocompleteSuggestion, false, false, true>({
    freeSolo: true,
    inputValue: currentValue,
    options: suggestions,
    filterOptions: (options) => options,
    getOptionLabel: suggestionLabel,
    getOptionKey: suggestionKey,
    onInputChange: (_event, value, reason) => {
      setValue(name, value as PathValue<T, Name>, {
        shouldDirty: true,
        shouldValidate: true,
      });
      if (reason === 'input') {
        setIsSearching(true);
        search(value);
      } else {
        setIsSearching(false);
        clearSuggestions();
      }
    },
    onChange: (_event, value) => {
      if (!value || typeof value === 'string') return;
      setIsSearching(false);
      void resolvePlace(value).then((resolved) => {
        const finalValue = resolved ?? suggestionLabel(value);
        setValue(name, finalValue as PathValue<T, Name>, {
          shouldDirty: true,
          shouldValidate: true,
        });
        clearSuggestions();
      });
    },
  });

  const { ref: autocompleteRootRef, ...autocompleteRootProps } =
    getRootProps() as React.ComponentPropsWithRef<'div'>;
  const rootRef = useForkRef(setAnchorEl, autocompleteRootRef);
  const inputProps = getInputProps();
  const { onClick: onClearClick } = getClearProps();

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClearClick?.(event);
    setValue(name, '' as PathValue<T, Name>, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsSearching(false);
    clearSuggestions();
  };

  const renderOption = (
    options: GetOptionsType,
    option: PlaceAutocompleteSuggestion,
  ) => {
    const { key, ...rest } = options;
    return (
      <li key={key} {...rest}>
        {option.name && <div className={styles.venueName}>{option.name}</div>}
        <div className={styles.address}>{option.address}</div>
      </li>
    );
  };

  return (
    <div className={styles.fieldContainer}>
      <ControlledFormInputAutocomplete<T, Name>
        control={control}
        name={name}
        label={label}
        type="text"
        inputRootRef={rootRef}
        inputRootSlotProps={{ ...autocompleteRootProps }}
        inputSlotProps={{ ...inputProps, placeholder }}
        labelClassName={labelClassName}
        darkMode={darkMode}
        endAdornment={
          currentValue ? (
            <IconButton
              ariaLabel="Clear search"
              iconName="close"
              className={styles.clearButton}
              iconClassName={styles.clearIcon}
              onClick={handleClear}
            />
          ) : null
        }
      />
      {anchorEl && isSearching && (
        <Popper
          open={popupOpen}
          anchorEl={anchorEl}
          className={styles.popperContainer}
          style={{ width: anchorEl.clientWidth }}
        >
          <div className={styles.popper}>
            {loading && <div className={styles.status}>Loading...</div>}
            {!loading && groupedOptions.length > 0 && (
              <FormListbox {...getListboxProps()} darkMode>
                {(groupedOptions as PlaceAutocompleteSuggestion[]).map(
                  (option, index) => {
                    const options = getOptionProps({
                      option,
                      index,
                    }) as GetOptionsType;
                    return renderOption(options, option);
                  },
                )}
              </FormListbox>
            )}
            {!loading && groupedOptions.length === 0 && (
              <div className={styles.status}>No results found...</div>
            )}
            {!loading && groupedOptions.length > 0 && (
              // Google Places policy: suggestions shown without a Google map must carry Google Maps attribution.
              <div className={styles.attribution} translate="no">
                Google Maps
              </div>
            )}
          </div>
        </Popper>
      )}
    </div>
  );
};
